(() => {
  if (window.PolygonsUI) return;
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let data = {};
  try { data = JSON.parse($('#PolygonsData')?.textContent || '{}'); } catch {}
  const i18n = (key, fallback) => data.i18n?.[key] || fallback;
  const focusableSelector = 'button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const visible = (element) => element.getClientRects().length > 0 && !element.closest('[hidden], [inert]');
  const focusables = (sheet) => $$(focusableSelector, sheet).filter(visible);
  let currentSheet = null;
  let returnFocus = null;
  let savedOverflow = '';
  let clearMediaTimer;
  const inertElements = new Map();
  const makeBackgroundInert = (sheet) => {
    let branch = sheet;
    let parent = branch.parentElement;
    while (parent && parent !== document.documentElement) {
      for (const sibling of parent.children) {
        if (sibling === branch || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) continue;
        inertElements.set(sibling, sibling.inert);
        sibling.inert = true;
      }
      branch = parent;
      parent = parent.parentElement;
    }
  };
  const restoreBackground = () => {
    for (const [element, previous] of inertElements) element.inert = previous;
    inertElements.clear();
  };
  const closeSheet = (sheet = currentSheet, restore = true) => {
    if (!sheet || !sheet.classList.contains('is-open')) return;
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    $$('video', sheet).forEach((video) => video.pause());
    if (sheet.dataset.sheet === 'modal') {
      clearMediaTimer = setTimeout(() => {
        if (!sheet.classList.contains('is-open')) $('[data-modal-content]', sheet)?.replaceChildren();
      }, motion.matches ? 0 : 300);
    }
    if (sheet !== currentSheet) return;
    currentSheet = null;
    restoreBackground();
    document.documentElement.style.overflow = savedOverflow;
    const previous = returnFocus;
    returnFocus = null;
    if (restore && previous?.isConnected && visible(previous)) previous.focus({ preventScroll: true });
    sheet.inert = true;
    sheet.dispatchEvent(new CustomEvent('polygons:sheet-close', { bubbles: true }));
  };
  const openSheet = (name) => {
    const sheet = $$('[data-sheet]').find((element) => element.dataset.sheet === name);
    if (!sheet) return false;
    if (currentSheet === sheet) return true;
    const previous = currentSheet ? returnFocus : document.activeElement;
    if (currentSheet) closeSheet(currentSheet, false);
    clearTimeout(clearMediaTimer);
    savedOverflow = document.documentElement.style.overflow;
    returnFocus = previous;
    currentSheet = sheet;
    sheet.inert = false;
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-hidden', 'false');
    sheet.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    makeBackgroundInert(sheet);
    requestAnimationFrame(() => {
      if (currentSheet !== sheet) return;
      const initial = $('[autofocus]', sheet);
      const target = initial && visible(initial) ? initial : focusables(sheet)[0];
      if (target) target.focus({ preventScroll: true });
      else { sheet.tabIndex = -1; sheet.focus({ preventScroll: true }); }
    });
    sheet.dispatchEvent(new CustomEvent('polygons:sheet-open', { bubbles: true }));
    return true;
  };
  const openMedia = (src, alt = '') => {
    const box = $('[data-modal-content]');
    if (!box || !src) return;
    let url;
    try { url = new URL(src, location.href); } catch { return; }
    if (!['https:', 'http:'].includes(url.protocol)) return;
    const isVideo = /\.mp4$/i.test(url.pathname);
    const media = document.createElement(isVideo ? 'video' : 'img');
    media.src = url.href;
    if (isVideo) {
      media.controls = true;
      media.playsInline = true;
      media.preload = 'metadata';
    } else media.alt = alt;
    box.replaceChildren(media);
    if (openSheet('modal') && isVideo) media.play().catch(() => {});
  };
  let toastTimer;
  const toast = (message) => {
    const element = $('[data-toast-el]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => element.classList.remove('is-visible'), 4200);
  };
  window.PolygonsUI = Object.freeze({ open: openSheet, close: () => closeSheet(), toast });
  $$('[data-sheet]').forEach((sheet) => sheet.setAttribute('aria-hidden', String(!sheet.classList.contains('is-open'))));
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-open]');
    if (opener) { event.preventDefault(); openSheet(opener.dataset.open); return; }
    const closer = event.target.closest('[data-close]');
    if (closer) { event.preventDefault(); closeSheet(closer.closest('[data-sheet]')); return; }
    const lightbox = event.target.closest('[data-lightbox]');
    if (lightbox) { event.preventDefault(); openMedia(lightbox.dataset.lightbox, $('img', lightbox)?.alt || ''); return; }
    const video = event.target.closest('[data-video]');
    if (video) { event.preventDefault(); openMedia(video.dataset.video); return; }
    if (event.target.closest('.nav-sheet__panel a[href]')) closeSheet();
  });
  document.addEventListener('keydown', (event) => {
    if (!currentSheet) return;
    if (event.key === 'Escape') { event.preventDefault(); closeSheet(); return; }
    if (event.key !== 'Tab') return;
    const items = focusables(currentSheet);
    if (!items.length) { event.preventDefault(); currentSheet.focus(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !currentSheet.contains(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !currentSheet.contains(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  });
  document.addEventListener('focusin', (event) => {
    if (!currentSheet || currentSheet.contains(event.target)) return;
    const target = focusables(currentSheet)[0] || currentSheet;
    target.focus({ preventScroll: true });
  });
  const header = $('[data-header]');
  let headerFrame;
  const updateHeader = () => header?.classList.toggle('is-scrolled', scrollY > 40);
  addEventListener('scroll', () => {
    if (headerFrame) return;
    headerFrame = requestAnimationFrame(() => { headerFrame = 0; updateHeader(); });
  }, { passive: true });
  updateHeader();
  const seenReveals = new WeakSet();
  const seenVideos = new WeakSet();
  const seenCounters = new WeakSet();
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;
  const visibleVideos = new Set();
  const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const { target, isIntersecting } of entries) {
      if (isIntersecting) visibleVideos.add(target);
      else visibleVideos.delete(target);
      if (isIntersecting && !motion.matches && !document.hidden) target.play().catch(() => {});
      else target.pause();
    }
  }, { threshold: 0.35 }) : null;
  const locale = document.documentElement.lang || 'en';
  let compact;
  try { compact = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }); }
  catch { compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }); }
  const countUp = (target) => {
    const end = Number(target.dataset.countup);
    if (!Number.isFinite(end)) return;
    const format = (number, done) => target.hasAttribute('data-compact') ? `${compact.format(number)}${done ? '+' : ''}` : number.toLocaleString(locale);
    if (motion.matches) { target.textContent = format(end, true); return; }
    const start = performance.now();
    const step = (now) => {
      const progress = motion.matches ? 1 : Math.min(1, (now - start) / 1800);
      target.textContent = format(Math.round(end * (1 - Math.pow(1 - progress, 4))), progress === 1);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const countObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      countObserver.unobserve(entry.target);
      countUp(entry.target);
    }
  }, { threshold: 0.6 }) : null;
  const observeContents = (root) => {
    const candidates = (selector) => root instanceof Element && root.matches(selector) ? [root, ...$$(selector, root)] : $$(selector, root);
    for (const element of candidates('.reveal')) {
      if (seenReveals.has(element)) continue;
      seenReveals.add(element);
      if (motion.matches || !revealObserver) element.classList.add('is-in');
      else revealObserver.observe(element);
    }
    for (const video of candidates('video[data-autoplay]')) {
      if (seenVideos.has(video)) continue;
      seenVideos.add(video);
      video.muted = true;
      if (videoObserver) videoObserver.observe(video);
    }
    for (const counter of candidates('[data-countup]')) {
      if (seenCounters.has(counter)) continue;
      seenCounters.add(counter);
      if (countObserver && !motion.matches) countObserver.observe(counter);
      else countUp(counter);
    }
  };
  observeContents(document);
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) for (const node of mutation.addedNodes) if (node instanceof Element) observeContents(node);
    }).observe(document.body, { childList: true, subtree: true });
  }
  const syncVideos = () => {
    for (const video of visibleVideos) {
      if (!motion.matches && !document.hidden) video.play().catch(() => {});
      else video.pause();
    }
    if (motion.matches) $$('.reveal').forEach((element) => element.classList.add('is-in'));
  };
  motion.addEventListener('change', syncVideos);
  document.addEventListener('visibilitychange', syncVideos);
  const pressItems = $$('.press__item');
  const pressTabs = $$('[data-press-tab]');
  const press = $('[data-press]')?.closest('.press');
  let pressIndex = 0;
  let pressTimer;
  let manualPress = false;
  const showPress = (index) => {
    pressIndex = (index + pressItems.length) % pressItems.length;
    pressItems.forEach((item, i) => {
      item.id ||= `polygons-press-panel-${i}`;
      item.setAttribute('role', 'tabpanel');
      item.setAttribute('aria-hidden', String(i !== pressIndex));
      item.classList.toggle('is-active', i === pressIndex);
    });
    pressTabs.forEach((tab, i) => {
      tab.id ||= `polygons-press-tab-${i}`;
      tab.setAttribute('aria-selected', String(i === pressIndex));
      tab.setAttribute('aria-controls', pressItems[i]?.id || '');
      pressItems[i]?.setAttribute('aria-labelledby', tab.id);
      tab.tabIndex = i === pressIndex ? 0 : -1;
    });
  };
  const stopPress = () => { clearInterval(pressTimer); pressTimer = null; };
  const startPress = () => {
    stopPress();
    if (!motion.matches && !manualPress && !document.hidden && pressItems.length > 1) pressTimer = setInterval(() => showPress(pressIndex + 1), 5000);
  };
  if (pressItems.length) {
    showPress(0);
    pressTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => { manualPress = true; stopPress(); showPress(index); });
      tab.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % pressTabs.length;
        if (event.key === 'ArrowLeft') next = (index + pressTabs.length - 1) % pressTabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = pressTabs.length - 1;
        if (next === undefined) return;
        event.preventDefault(); manualPress = true; stopPress(); showPress(next); pressTabs[next].focus();
      });
    });
    press?.addEventListener('pointerenter', stopPress);
    press?.addEventListener('pointerleave', startPress);
    press?.addEventListener('focusin', stopPress);
    press?.addEventListener('focusout', (event) => { if (!press.contains(event.relatedTarget)) startPress(); });
    motion.addEventListener('change', startPress);
    document.addEventListener('visibilitychange', startPress);
    startPress();
  }
  $$('[data-unit]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.classList.contains('is-active')));
    button.addEventListener('click', () => {
      const root = button.closest('[data-units]');
      if (!root) return;
      const unit = button.dataset.unit;
      const other = unit === 'us' ? 'metric' : 'us';
      $$('[data-unit]', root).forEach((item) => {
        item.classList.toggle('is-active', item === button);
        item.setAttribute('aria-pressed', String(item === button));
      });
      $$('.measure__list li', root).forEach((item) => {
        const primary = $('span', item);
        const secondary = $('small', item);
        if (primary) primary.textContent = item.dataset[unit];
        if (secondary) secondary.textContent = item.dataset[other];
      });
    });
  });
  $$('[data-scroll]').forEach((button) => button.addEventListener('click', () => {
    const scroller = $('[data-scroller]', button.closest('section') || document);
    if (!scroller) return;
    scroller.scrollBy({ left: Number(button.dataset.scroll) * scroller.clientWidth * 0.7, behavior: motion.matches ? 'instant' : 'smooth' });
  }));
  $$('[data-scroller]').forEach((scroller) => {
    scroller.tabIndex = 0;
    scroller.addEventListener('keydown', (event) => {
      if (event.target !== scroller || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      scroller.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * scroller.clientWidth * 0.7, behavior: motion.matches ? 'instant' : 'smooth' });
    });
  });
  const syncSound = (card, muted) => {
    const button = $('.ugc__sound', card);
    const icon = $('.ugc__sound use', card);
    button?.setAttribute('aria-label', i18n(muted ? 'unmute' : 'mute', muted ? 'Unmute' : 'Mute'));
    button?.setAttribute('aria-pressed', String(!muted));
    icon?.setAttribute('href', muted ? '#i-sound-off' : '#i-sound-on');
  };
  $$('.ugc__card').forEach((card) => {
    const video = $('video', card);
    if (!video) return;
    syncSound(card, true);
    card.addEventListener('click', () => {
      const unmute = video.muted;
      $$('.ugc__card').forEach((other) => {
        const otherVideo = $('video', other);
        if (otherVideo) otherVideo.muted = true;
        syncSound(other, true);
      });
      video.muted = !unmute;
      syncSound(card, video.muted);
      if (unmute) video.play().catch(() => {});
    });
  });
})();
